import { toast } from "@/components/common/Toast"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { CONVERSATION_TITLE_MAX_LENGTH } from "@/constants/UiConstants"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useRenameChatConversation } from "@/services/api/chatConversationService"
import { isLimitError } from "@/utils/limitErrors"
import React from "react"

interface RenameConversationDialogProps {
  conversationId: string
  currentTitle: string
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
}

export const RenameConversationDialog: React.FC<
  RenameConversationDialogProps
> = ({ conversationId, currentTitle, isOpen, setIsOpen }) => {
  const { translate } = useTranslation()
  const renameMutation = useRenameChatConversation()
  const [nextTitle, setNextTitle] = React.useState(currentTitle)

  React.useEffect(() => {
    setNextTitle(currentTitle)
  }, [currentTitle])

  const handleRename = async () => {
    const trimmedTitle = nextTitle.trim()
    if (!trimmedTitle) {
      toast.error(translate("AI_CHAT/HISTORY/RENAME_EMPTY_ERROR"))
      return
    }

    try {
      await renameMutation.mutateAsync({
        conversationId,
        payload: { title: trimmedTitle },
      })
      setIsOpen(false)
      toast.success(translate("AI_CHAT/HISTORY/RENAME_SUCCESS"))
    } catch (error) {
      if (!isLimitError(error)) {
        toast.error(translate("AI_CHAT/HISTORY/RENAME_ERROR"))
      }
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{translate("AI_CHAT/HISTORY/RENAME_TITLE")}</DialogTitle>
          <DialogDescription>
            {translate("AI_CHAT/HISTORY/RENAME_DESCRIPTION")}
          </DialogDescription>
        </DialogHeader>
        <Input
          value={nextTitle}
          onChange={(event) => setNextTitle(event.target.value)}
          maxLength={CONVERSATION_TITLE_MAX_LENGTH}
          placeholder={translate("AI_CHAT/HISTORY/RENAME_PLACEHOLDER")}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault()
              void handleRename()
            }
          }}
        />
        <DialogFooter>
          <Button variant="outline" onClick={() => setIsOpen(false)}>
            {translate("AI_CHAT/HISTORY/CANCEL")}
          </Button>
          <Button onClick={handleRename} disabled={renameMutation.isPending}>
            {translate("AI_CHAT/HISTORY/SAVE")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
